import { Directive, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import * as selectors from '../../store/reducers/index';
import { OrganisationPreferenceDto } from '@careapp-group/ng-sdk-v2-new';

/**
 * Will only display the element if the current organisation preference value
 * matches the value passed in. This will either retrieve values from the server
 * or default to false.
 *
 * Usage:
 *
 * 1. Within your .ts file, import the OrganisationPreferenceDirectiveCheck:
 * > import { OrganisationPreferenceDirectiveCheck } from '.../organisation-preference.directive';
 *
 * 2. Create an object describing the key/values expected:
 * > testOrgPreferenceItem: OrganisationPreferenceDirectiveCheck =
 * >   { key: 'familyCanPostContent', expected: true };
 *
 * 3. In your html, the directive can now be used:
 * >  <div *orgPreference="testPreferenceItem">
 * >    Hello world!
 * >  </div>
 */

export interface OrganisationPreferenceDirectiveCheck {
  key: string;
  expected: any;
}

@Directive({ selector: '[orgPreference]' })
export class OrganisationPreferenceDirective implements OnDestroy {
  orgPreference$: Observable<any>;
  orgPreferenceSubscription: Subscription;
  preferenceData: OrganisationPreferenceDto;
  isSatisfied: boolean;
  _rendered: boolean;

  constructor(
    public store: Store<any>,
    private templateRef: TemplateRef<any>,
    private viewContainerRef: ViewContainerRef
  ) {
    this.orgPreference$ = this.store.select(selectors.org.currentOrgPreferences);
    this.orgPreferenceSubscription = this.orgPreference$.subscribe((preference) => {
      this.preferenceData = <any>{
        ...(preference || {}),
      };
      this._updateView();
    });
  }

  private _condition: OrganisationPreferenceDirectiveCheck;
  get condition() {
    return this._condition;
  }

  @Input() set orgPreference(condition: OrganisationPreferenceDirectiveCheck) {
    this._condition = condition;
    this._updateView();
  }

  private _updateView() {
    const preference = this.preferenceData || {};
    const { expected, key } = this.condition || { key: '', expected: true };
    if (preference[key] === expected && !this._rendered) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
      this._rendered = true;
    } else if (preference[key] !== expected && this._rendered) {
      this.viewContainerRef.clear();
      this._rendered = false;
    }
  }

  ngOnDestroy() {
    this.orgPreferenceSubscription.unsubscribe();
  }
}
